"use client";

import { useState, useCallback } from "react";
import { Trash2 } from "lucide-react";
import { Input } from "@stockline/ui/components/input";
import { useProposalMutations } from "@stockline/web";
import { useTranslation } from "react-i18next";
import type { Proposal } from "@stockline/shared/types/ai/proposal";
import type { ProposalCardInnerProps } from "./proposal-card-shared";
import { ProposalCardShell } from "./ProposalCardShell";

interface PriceEdit {
  proposalId: string;
  value: string;
}

function getProductName(proposal: Proposal): string {
  const fields = proposal.payload?.display_fields;
  if (Array.isArray(fields)) {
    const typed = fields as Array<{ label: string; value: string }>;
    const productField = typed.find((f) => f.label === "product");
    if (productField) return productField.value;
    // Batch-summary proposal: extract from line_1 (format: "Product Name · Price €")
    const lineField = typed.find(
      (f) => f.label === `line_${(proposal.sequence ?? 0) + 1}`,
    );
    if (lineField) {
      const parts = lineField.value.split(" · ");
      if (parts.length > 0) return parts[0];
    }
  }
  return String(proposal.payload?.product_name ?? proposal.payload?.ask ?? "");
}

function getPrice(proposal: Proposal): string {
  const price = proposal.payload?.price;
  if (price === null || price === undefined) return "";
  return String(price);
}

export function ProposalPriceCard(props: ProposalCardInnerProps) {
  const { batch } = props;
  const { t } = useTranslation();
  const { editProposal, removeProposal } = useProposalMutations();
  const [editing, setEditing] = useState<PriceEdit | null>(null);

  const isPending = batch.status === "pending";

  const handlePriceClick = useCallback(
    (proposalId: string, currentPrice: string) => {
      if (!isPending) return;
      setEditing({ proposalId, value: currentPrice });
    },
    [isPending],
  );

  const handleSave = useCallback(async () => {
    if (!editing) return;
    const inFlight = editing;
    const parsed = Number(inFlight.value);
    if (Number.isNaN(parsed) || inFlight.value.trim() === "") {
      setEditing((curr) => (curr === inFlight ? null : curr));
      return;
    }
    await editProposal(batch.id, inFlight.proposalId, { price: parsed });
    // Only clear if the user hasn't started a newer edit while the save was
    // in flight (different proposalId or a fresh value).
    setEditing((curr) => (curr === inFlight ? null : curr));
  }, [editing, batch.id, editProposal]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleSave().catch(console.error);
      else if (e.key === "Escape") setEditing(null);
    },
    [handleSave],
  );

  const handleRemove = useCallback(
    (proposalId: string) => {
      removeProposal(batch.id, proposalId).catch(console.error);
    },
    [batch.id, removeProposal],
  );

  return (
    <ProposalCardShell {...props}>
      <div className="px-3 pb-2">
        <div className="rounded-md bg-background/60 border border-border/50 overflow-hidden">
          {/* Table header */}
          <div className="flex items-center gap-1 px-2 py-1 bg-muted/30 border-b border-border/30 text-xs text-muted-foreground font-medium">
            <span className="flex-1 min-w-0">
              {t("aiProposals.field.product")}
            </span>
            <span className="w-20 text-right">
              {t("aiProposals.field.price")}
            </span>
            {isPending && <span className="w-6" />}
          </div>

          {/* Price rows */}
          {batch.proposals.map((proposal) => (
            <PriceRow
              key={proposal.id}
              proposal={proposal}
              isPending={isPending}
              editing={editing}
              onPriceClick={handlePriceClick}
              onSave={handleSave}
              onKeyDown={handleKeyDown}
              onEditingChange={setEditing}
              onRemove={handleRemove}
            />
          ))}
        </div>

        {/* Summary */}
        <div className="mt-1 text-xs text-muted-foreground text-right">
          {batch.proposals.length} {t("aiProposals.field.lines").toLowerCase()}
        </div>
      </div>
    </ProposalCardShell>
  );
}

function PriceRow({
  proposal,
  isPending,
  editing,
  onPriceClick,
  onSave,
  onKeyDown,
  onEditingChange,
  onRemove,
}: {
  readonly proposal: Proposal;
  readonly isPending: boolean;
  readonly editing: PriceEdit | null;
  readonly onPriceClick: (id: string, price: string) => void;
  readonly onSave: () => Promise<void>;
  readonly onKeyDown: (e: React.KeyboardEvent) => void;
  readonly onEditingChange: (e: PriceEdit | null) => void;
  readonly onRemove: (id: string) => void;
}) {
  const productName = getProductName(proposal);
  const price = getPrice(proposal);
  const isEditing = editing?.proposalId === proposal.id;

  return (
    <div
      className="flex items-center gap-1 px-2 py-1 text-sm border-b border-border/30 last:border-b-0 group hover:bg-muted/10"
      data-testid={`proposal-price-row-${proposal.id}`}
    >
      <span
        className="flex-1 min-w-0 truncate text-xs font-medium"
        title={productName}
      >
        {productName}
      </span>

      <div className="w-20 text-right shrink-0">
        <PriceValue
          proposalId={proposal.id}
          price={price}
          isEditing={isEditing}
          isPending={isPending}
          editing={editing}
          onEditingChange={onEditingChange}
          onKeyDown={onKeyDown}
          onSave={onSave}
          onPriceClick={onPriceClick}
        />
      </div>

      {isPending && (
        <button
          onClick={() => onRemove(proposal.id)}
          className="w-6 flex items-center justify-center opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity shrink-0"
          title="Remove"
          data-testid={`proposal-price-remove-${proposal.id}`}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function PriceValue({
  proposalId,
  price,
  isEditing,
  isPending,
  editing,
  onEditingChange,
  onKeyDown,
  onSave,
  onPriceClick,
}: {
  readonly proposalId: string;
  readonly price: string;
  readonly isEditing: boolean;
  readonly isPending: boolean;
  readonly editing: PriceEdit | null;
  readonly onEditingChange: (e: PriceEdit | null) => void;
  readonly onKeyDown: (e: React.KeyboardEvent) => void;
  readonly onSave: () => Promise<void>;
  readonly onPriceClick: (id: string, price: string) => void;
}) {
  if (isEditing && editing) {
    return (
      <Input
        value={editing.value}
        onChange={(e) => onEditingChange({ ...editing, value: e.target.value })}
        onKeyDown={onKeyDown}
        onBlur={() => onSave().catch(console.error)}
        type="number"
        className="h-6 text-xs px-1 text-right"
        data-testid={`proposal-price-edit-${proposalId}`}
        autoFocus
      />
    );
  }

  if (isPending) {
    return (
      <button
        type="button"
        className="text-xs font-medium bg-transparent border-0 p-0 cursor-pointer hover:text-primary"
        onClick={() => onPriceClick(proposalId, price)}
        data-testid={`proposal-price-value-${proposalId}`}
      >
        {price ? `${price} €` : "-"}
      </button>
    );
  }

  return (
    <span
      className="text-xs font-medium"
      data-testid={`proposal-price-value-${proposalId}`}
    >
      {price ? `${price} €` : ""}
    </span>
  );
}
